import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bot, Calendar, ShieldCheck, AlertTriangle, Coins } from "lucide-react";
import { cn } from "@/utils/cn";
import { agentApiClient } from "@/services/agentApiClient";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { setQuotaStatus } from "@/store/slices/aiChatSlice";
import { fetchCreditBalance } from "@/store/slices/aiCreditsSlice";

interface AiUsageStatus {
  percentUsed: number;
  resetAt: string;
  periodMonth: string;
  numQueriesThisMonth?: number;
}

function formatResetDate(iso?: string, locale = "fr-FR"): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return "—";
  }
}

function formatPeriod(periodMonth?: string, locale = "fr-FR"): string {
  if (!periodMonth) return "—";
  try {
    const [y, m] = periodMonth.split("-").map(Number);
    return new Intl.DateTimeFormat(locale, {
      month: "long",
      year: "numeric",
    }).format(new Date(Date.UTC(y, (m ?? 1) - 1, 1)));
  } catch {
    return periodMonth;
  }
}

function formatEurAmount(value: number, locale = "fr-FR"): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(value);
}

function daysUntil(iso?: string): number | null {
  if (!iso) return null;
  try {
    const target = new Date(iso).getTime();
    const now = Date.now();
    return Math.max(0, Math.ceil((target - now) / (1000 * 60 * 60 * 24)));
  } catch {
    return null;
  }
}

export const AiUsageCard: React.FC = () => {
  const dispatch = useAppDispatch();
  // Subscribe to the shared quota state so a chat happening elsewhere in the
  // app (e.g. AiChatPanel emitting the post-result quota_status event) updates
  // this card in real-time without re-fetching.
  const { t, i18n } = useTranslation();
  const reduxQuota = useAppSelector((s) => s.aiChat.quotaStatus);
  const creditsBalance = useAppSelector((s) => s.aiCredits.balance);
  const [fetched, setFetched] = useState<AiUsageStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Solde de crédits : quand il existe, les alertes de saturation du quota
  // changent de ton (les crédits prennent le relais, rien d'alarmant).
  useEffect(() => {
    if (creditsBalance === null) {
      dispatch(fetchCreditBalance());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);
  const creditsUsd = creditsBalance?.balanceUsd ?? 0;

  // Prefer the live Redux state (updated by useAgentStream on every chat) but
  // fall back to the dedicated /api/quota fetch when the user hasn't started a
  // chat session yet in this browser tab.
  const status: AiUsageStatus | null = reduxQuota ?? fetched;

  // Skip the initial fetch when Redux already holds fresh data from a chat
  // that ran earlier in this tab — that value is at least as fresh as anything
  // /api/quota would return.
  const hasLiveQuota = Boolean(reduxQuota);

  useEffect(() => {
    if (hasLiveQuota) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await agentApiClient.get("/api/quota");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as { data?: AiUsageStatus };
        if (cancelled) return;
        const data = body.data ?? null;
        setFetched(data);
        if (data) {
          dispatch(
            setQuotaStatus({
              percentUsed: data.percentUsed,
              resetAt: data.resetAt,
              periodMonth: data.periodMonth,
            }),
          );
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : t("common.error"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // hasLiveQuota intentionally excluded — we only run this fetch logic at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // When live Redux data arrives, no need to keep showing the skeleton even if
  // the initial fetch is still in flight.
  const effectiveLoading = loading && !status;

  const pct = Math.max(0, Math.min(100, status?.percentUsed ?? 0));
  const remainingPct = Math.max(0, 100 - pct);
  const daysLeft = daysUntil(status?.resetAt);
  const locale = i18n.language === "en" ? "en-GB" : "fr-FR";
  const resetLabel = formatResetDate(status?.resetAt, locale);
  const periodLabel = formatPeriod(status?.periodMonth, locale);

  // Jauge crédits : restant rapporté au total acheté (réservoir séparé du
  // quota mensuel, sans expiration).
  const totalPurchasedUsd = creditsBalance?.totalPurchasedUsd ?? 0;
  const creditsPct =
    totalPurchasedUsd > 0
      ? Math.max(0, Math.min(100, Math.round((creditsUsd / totalPurchasedUsd) * 100)))
      : 0;
  // La jauge du quota inclus garde ses couleurs honnêtes (rouge à saturation) :
  // la jauge crédits juste en dessous montre la réserve qui prend le relais.
  const quotaBarColor =
    pct >= 90
      ? "bg-red-500 dark:bg-red-500"
      : pct >= 60
        ? "bg-amber-500 dark:bg-amber-500"
        : "bg-emerald-500 dark:bg-emerald-500";

  // Color scheme based on usage level. Le rouge/orange signale un risque de
  // blocage — avec un solde de crédits, il n'y en a aucun : la carte reste
  // verte (badge « Crédits actifs »), seul le % dit où en est le quota inclus.
  const tone = (
    creditsUsd > 0
      ? {
          ring: "ring-emerald-200 dark:ring-emerald-900/40",
          bg: "bg-emerald-50 dark:bg-emerald-950/30",
          iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
          iconText: "text-emerald-600 dark:text-emerald-400",
          bar: "bg-emerald-500 dark:bg-emerald-500",
          badge: "bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 border-emerald-300/50",
          label: t("aiAgent.quota.badgeCredits"),
        }
      : null
  ) ??
  (pct >= 90
      ? {
          ring: "ring-red-200 dark:ring-red-900/40",
          bg: "bg-red-50 dark:bg-red-950/30",
          iconBg: "bg-red-100 dark:bg-red-900/40",
          iconText: "text-red-600 dark:text-red-400",
          bar: "bg-red-500 dark:bg-red-500",
          badge: "bg-red-500/10 text-red-700 dark:bg-red-500/20 dark:text-red-300 border-red-300/50",
          label: t("aiAgent.quota.badgeCritical"),
        }
      : pct >= 60
        ? {
            ring: "ring-amber-200 dark:ring-amber-900/40",
            bg: "bg-amber-50 dark:bg-amber-950/30",
            iconBg: "bg-amber-100 dark:bg-amber-900/40",
            iconText: "text-amber-600 dark:text-amber-400",
            bar: "bg-amber-500 dark:bg-amber-500",
            badge: "bg-amber-500/10 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 border-amber-300/50",
            label: t("aiAgent.quota.badgeHigh"),
          }
        : {
            ring: "ring-emerald-200 dark:ring-emerald-900/40",
            bg: "bg-emerald-50 dark:bg-emerald-950/30",
            iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
            iconText: "text-emerald-600 dark:text-emerald-400",
            bar: "bg-emerald-500 dark:bg-emerald-500",
            badge: "bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 border-emerald-300/50",
            label: t("aiAgent.quota.badgeNormal"),
          });

  return (
    <Card className="border-border/60 overflow-hidden">
      <CardContent className="p-6 md:p-8">
        {effectiveLoading ? (
          <div className="space-y-4" aria-busy="true">
            <div className="h-4 w-32 bg-muted rounded animate-pulse" />
            <div className="h-10 w-40 bg-muted rounded animate-pulse" />
            <div className="h-2 w-full bg-muted rounded animate-pulse" />
          </div>
        ) : error ? (
          <div className="flex items-start gap-3 p-3 rounded-lg border border-muted bg-muted/30">
            <AlertTriangle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">
              {t("aiAgent.quota.errorLoad")}
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-12 md:gap-8 items-start">
            {/* LEFT: section title + big % + status badge */}
            <div className="md:col-span-5 space-y-3">
              <div className="flex items-center gap-2">
                <Bot
                  className="h-4 w-4 text-muted-foreground"
                  aria-hidden="true"
                />
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {t("aiAgent.quota.sectionLabel")}
                </p>
              </div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span
                  className={cn(
                    "text-5xl md:text-6xl font-bold leading-none tabular-nums",
                    tone.iconText,
                  )}
                >
                  {pct}
                  <span className="text-2xl ml-0.5 font-semibold">%</span>
                </span>
                <Badge
                  variant="outline"
                  className={cn("capitalize border", tone.badge)}
                >
                  {tone.label}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {t("aiAgent.quota.usedIn", { period: periodLabel })}
              </p>
            </div>

            {/* RIGHT: deux jauges distinctes (quota inclus / crédits) + détails */}
            <div className="md:col-span-7 space-y-5">
              {/* Jauge 1 — quota mensuel inclus dans l'abonnement */}
              <div>
                <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  {t("aiAgent.quota.includedBarLabel")}
                </p>
                <div
                  className="h-2.5 w-full overflow-hidden rounded-full bg-muted"
                  role="progressbar"
                  aria-valuenow={pct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`Quota mensuel utilisé à ${pct}%`}
                >
                  <div
                    className={cn(
                      "h-full transition-all duration-500 ease-out",
                      quotaBarColor,
                    )}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground tabular-nums">
                  <span>0%</span>
                  <span className="font-medium">
                    {remainingPct > 1
                      ? t("aiAgent.quota.remaining", { pct: remainingPct })
                      : t("aiAgent.quota.remainingSingular", { pct: remainingPct })}
                  </span>
                  <span>100%</span>
                </div>
              </div>

              {/* Jauge 2 — crédits achetés (affichée dès qu'il y en a eu) */}
              {totalPurchasedUsd > 0 && (
                <div>
                  <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    {t("aiAgent.quota.creditsBarLabel")}
                  </p>
                  <div
                    className="h-2.5 w-full overflow-hidden rounded-full bg-muted"
                    role="progressbar"
                    aria-valuenow={creditsPct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={t("aiAgent.quota.creditsBarLabel")}
                  >
                    <div
                      className="h-full transition-all duration-500 ease-out bg-emerald-500 dark:bg-emerald-500"
                      style={{ width: `${creditsPct}%` }}
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground tabular-nums">
                    <span className="font-medium text-emerald-700 dark:text-emerald-300">
                      {t("aiAgent.quota.creditsRemaining", {
                        amount: formatEurAmount(creditsUsd, locale),
                      })}
                    </span>
                    <span>
                      {t("aiAgent.quota.creditsPurchased", {
                        amount: formatEurAmount(totalPurchasedUsd, locale),
                      })}
                    </span>
                  </div>
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex items-start gap-2.5">
                  <Calendar
                    className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0"
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      {t("aiAgent.quota.reinitLabel")}
                    </p>
                    <p className="text-sm font-medium mt-0.5 truncate">
                      {resetLabel}
                    </p>
                    {daysLeft != null && daysLeft <= 7 && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t("aiAgent.quota.inDays_other", { count: daysLeft })}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-2.5">
                  <ShieldCheck
                    className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0"
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      {t("aiAgent.quota.statusLabel")}
                    </p>
                    <p className="text-sm font-medium mt-0.5 truncate">
                      {pct >= 100 && creditsUsd <= 0
                        ? t("aiAgent.quota.statusReached")
                        : t("aiAgent.quota.statusActive")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Avec un solde de crédits, la saturation du quota inclus n'est
                  pas bloquante : message rassurant plutôt qu'alarme. */}
              {pct >= 90 && creditsUsd > 0 && (
                <div
                  className="flex items-start gap-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50 dark:border-emerald-900/40 dark:bg-emerald-950/30"
                  role="status"
                >
                  <Coins className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-emerald-800 dark:text-emerald-200 leading-relaxed">
                    {t("aiAgent.quota.creditsTakeOver", {
                      amount: new Intl.NumberFormat(locale, {
                        style: "currency",
                        currency: "EUR",
                        maximumFractionDigits: 2,
                      }).format(creditsUsd),
                    })}
                  </p>
                </div>
              )}

              {pct >= 90 && pct < 100 && creditsUsd <= 0 && (
                <div
                  className="flex items-start gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30"
                  role="status"
                >
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                    {t("aiAgent.quota.warningNearLimit")}
                  </p>
                </div>
              )}

              {pct >= 100 && creditsUsd <= 0 && (
                <div
                  className="flex items-start gap-3 p-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30"
                  role="alert"
                >
                  <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-red-800 dark:text-red-200 leading-relaxed">
                    {t("aiAgent.quota.errorReached", { date: resetLabel })}
                  </p>
                </div>
              )}

            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
